<script setup>
import axios from 'axios';
import { computed, onMounted, ref, watch } from 'vue';
import { countOrNull } from '../../../helpers/contentCounts';
import Loading from '../../Loading.vue';
import XlButton from '../../xl/XlButton.vue';
import XlCard from '../../xl/XlCard.vue';
import XlModal from '../../xl/XlModal.vue';
import XlPagination from '../../xl/XlPagination.vue';
import XlTag from '../../xl/XlTag.vue';
import ContentRowMenu from '../../content/ContentRowMenu.vue';
import ManageAccessModal from '../../resource-access/ManageAccessModal.vue';
import AddedBy from '../../resource-access/AddedBy.vue';
import ResourceAccessTag from '../../resource-access/ResourceAccessTag.vue';
import { bookFor } from '../../../composables/useResourceAccess';
import LinkModal from './LinkModal.vue';
import useHubMembers from '../../../composables/useHubMembers';
import { CAPABILITIES, can, isBroker } from '../../../helpers/roles';
import { useAuthStore } from '../../../stores/auth';

/**
 * The list of links: title and description, where it goes, who sees it, how
 * often it was opened, and a word when the address did not answer the last
 * time it was saved. The editor takes the whole tab when it opens.
 */
const props = defineProps({
  actionsDisabled: { type: Boolean, default: false },
});
const emit = defineEmits(['count', 'editing', 'failed']);

const PAGE_SIZE = 10;

const api = () => window.store.event_backend_api;
const notify = (message, error = false) => window.loadNotification?.({ message, error });

const authStore = useAuthStore();
const canManage = computed(() => can(authStore.userRole, CAPABILITIES.CONTRIBUTE_HUB_CONTENT, authStore.capabilities));
// A broker sees every link that reaches their book but changes only their own.
const mine = (row) => !isBroker(authStore.userData) || row.created_by === authStore.userData?.subdomain_user_id;
const { members, loadHubMembers } = useHubMembers();

const links = ref([]);
const loading = ref(true);
const failed = ref(false);
const busy = ref(false);

const page = ref(1);

const modalOpen = ref(false);
const modalLink = ref(null);
const accessTarget = ref(null);
const deleting = ref([]);

// ---- data ----

const load = async () => {
  loading.value = true;
  failed.value = false;
  try {
    const { data } = await axios.get(`${api()}/event-links`, { params: { admin: 1 } });
    links.value = Array.isArray(data) ? data : (data?.data ?? []);
  } catch (error) {
    console.error(error);
    failed.value = true;
    notify('Could not load the links.', true);
  } finally {
    loading.value = false;
  }
};

onMounted(async () => {
  await load();
  await loadHubMembers();
});

watch([() => links.value.length, loading, failed], ([n, isLoading, hasFailed]) => emit('count', countOrNull(n, isLoading, hasFailed)), { immediate: true });
watch(failed, (hasFailed) => emit('failed', hasFailed), { immediate: true });

// ---- view ----

// The editor is a page of its own, so the tab tells the shell it is editing.
const pageEditor = computed(() => modalOpen.value);
watch(pageEditor, (on) => emit('editing', on), { immediate: true });

const people = (n) => `${new Intl.NumberFormat().format(n)} ${n === 1 ? 'person' : 'people'}`;

const filtered = computed(() => links.value);

const lastPage = computed(() => Math.max(1, Math.ceil(filtered.value.length / PAGE_SIZE)));
const currentPage = computed(() => Math.min(page.value, lastPage.value));
const paged = computed(() => filtered.value.slice((currentPage.value - 1) * PAGE_SIZE, currentPage.value * PAGE_SIZE));
const from = computed(() => (filtered.value.length ? (currentPage.value - 1) * PAGE_SIZE + 1 : 0));
const to = computed(() => Math.min(currentPage.value * PAGE_SIZE, filtered.value.length));

// ---- editor ----

const openAdd = () => {
  if (!canManage.value) return false;
  modalLink.value = null;
  modalOpen.value = true;
  return true;
};
const openEdit = (row) => {
  if (!canManage.value) return;
  modalLink.value = row;
  modalOpen.value = true;
};
const onSaved = async () => {
  modalOpen.value = false;
  await load();
};

// ---- audience ----

const openAccess = (row) => {
  if (!canManage.value) return;
  accessTarget.value = row;
};
const onAccessSaved = (access) => {
  const row = links.value.find((r) => r.id === accessTarget.value?.id);
  if (row) {
    row.access = access;
    if (access?.reach !== undefined) row.reach = access.reach;
  }
  accessTarget.value = null;
};

// ---- delete ----

const deleteCost = computed(() => {
  const chosen = deleting.value;
  const reach = chosen.reduce((sum, r) => sum + (r.reach ?? 0), 0);
  const noun = chosen.length === 1 ? 'this link' : 'these links';
  if (!reach) return `Nobody sees ${noun}.`;
  return `${people(reach)} will no longer see ${noun}.`;
});

const confirmDelete = (chosen) => {
  if (!canManage.value || props.actionsDisabled || !chosen.length) return;
  deleting.value = chosen;
};

const doDelete = async () => {
  const chosen = deleting.value;
  busy.value = true;
  try {
    await Promise.all(chosen.map((r) => axios.delete(`${api()}/event-links/${r.id}`)));
    const gone = new Set(chosen.map((r) => r.id));
    links.value = links.value.filter((r) => !gone.has(r.id));
    notify(chosen.length === 1 ? 'Link deleted' : `${chosen.length} links deleted`);
    deleting.value = [];
  } catch (error) {
    console.error(error);
    notify('Could not delete.', true);
    await load();
  } finally {
    busy.value = false;
  }
};

// ---- row menu ----

// Nothing on this menu is for anyone but a manager, so a viewer gets no menu
// at all rather than a menu of controls that would refuse them.
const menuRowId = ref(null);

const menuItems = computed(() => (canManage.value
  ? [
    { key: 'access', label: 'Who can see it' },
    { key: 'edit', label: 'Edit' },
    { key: 'delete', label: 'Delete', danger: true, disabled: props.actionsDisabled || busy.value },
  ]
  : []));

const onMenu = (row, key) => {
  if (key === 'access') openAccess(row);
  else if (key === 'edit') openEdit(row);
  else if (key === 'delete') confirmDelete([row]);
};

// The editor's own delete: step back to the list and ask there, so the
// confirmation reads the same wherever it was started from.
const deleteFromEditor = () => {
  const row = modalLink.value;
  modalOpen.value = false;
  if (row) confirmDelete([row]);
};

defineExpose({ openAdd });
</script>

<template>
  <div class="links-tab">
    <LinkModal
      v-if="pageEditor"
      layout="page"
      :link="modalLink"
      :members="members"
      @close="modalOpen = false"
      @saved="onSaved"
      @delete="deleteFromEditor"
    />

    <template v-else>
      <div class="links-tab__toolbar">
        <span class="links-tab__spacer" />
        <XlButton v-if="canManage" :disabled="actionsDisabled || busy" @click="openAdd">
          <template #icon>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
          </template>
          Add a link
        </XlButton>
      </div>

      <XlCard class="links-tab__card">
        <Loading v-if="loading" variant="rows" :rows="5" />

        <div v-else-if="!links.length" class="links-tab__empty">
          <b>No links yet</b>
          <span>Point policyholders to important pages and external sites.</span>
        </div>

        <div v-else class="links-tab__rows">
          <div v-for="row in paged" :key="row.id" class="links-tab__row" :class="{ 'has-menu': menuRowId === row.id }">
            <span class="links-tab__tile" aria-hidden="true">
              <img v-if="row.image_url" :src="row.image_url" alt="">
              <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 4h6v6" /><path d="M20 4 10 14" /><path d="M20 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h5" /></svg>
            </span>
            <div class="links-tab__row-text">
              <span class="links-tab__title"><b>{{ row.title }}</b><XlTag v-if="row.reachable === false" tone="warning">Can't reach</XlTag></span>
              <small v-if="row.description">{{ row.description }}</small>
              <small class="links-tab__where"><AddedBy :created-by="row.created_by" :name="row.creator_name" />{{ row.link }} · opens in a new tab</small>
            </div>
            <ResourceAccessTag :access="row.access" :members="members" :manageable="canManage" :book="bookFor(row, authStore.userData)" @manage="openAccess(row)" />
            <span class="links-tab__clicks">{{ row.click_count ?? 0 }} clicks</span>
            <div class="links-tab__row-actions">
              <ContentRowMenu v-if="canManage && mine(row)" :items="menuItems" @select="onMenu(row, $event)" @toggle="menuRowId = $event ? row.id : null" />
            </div>
          </div>
        </div>
      </XlCard>

      <p v-if="links.length" class="links-tab__count">
        {{ links.length }} {{ links.length === 1 ? 'link' : 'links' }} · they appear on the policyholder's Home.
      </p>

      <div v-if="lastPage > 1" class="links-tab__cards-foot">
        <XlPagination :from="from" :to="to" :total="filtered.length" :selected="0" @previous="page = Math.max(1, currentPage - 1)" @next="page = Math.min(lastPage, currentPage + 1)" />
      </div>

      <ManageAccessModal
        v-if="accessTarget"
        :resource="{ id: accessTarget.id, name: accessTarget.title ?? accessTarget.name, access: accessTarget.access, created_by: accessTarget.created_by, creator_name: accessTarget.creator_name }"
        :members="members"
        :book="bookFor(accessTarget, authStore.userData)"
        subject-type="link"
        @close="accessTarget = null"
        @saved="onAccessSaved"
      />

      <XlModal v-if="deleting.length" :title="deleting.length === 1 ? `Delete ${deleting[0].title}?` : `Delete ${deleting.length} links?`" max-width="480px" @close="deleting = []">
        <p class="links-tab__cost">{{ deleteCost }}</p>
        <template #actions>
          <XlButton variant="ghost" size="sm" @click="deleting = []">Cancel</XlButton>
          <XlButton variant="danger" size="sm" :disabled="busy" @click="doDelete">Delete</XlButton>
        </template>
      </XlModal>
    </template>
  </div>
</template>

<style scoped>
.links-tab { display: flex; flex-direction: column; gap: 14px; }

.links-tab__toolbar { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; }
.links-tab__search { flex: 1; min-width: 200px; }
.links-tab__spacer { flex: 1; }

.links-tab__card { margin-bottom: 0; overflow: hidden; }

.links-tab__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 48px 16px;
  text-align: center;
  color: var(--xl-color-secondary-text, #49454f);
}
.links-tab__empty b { color: var(--xl-color-primary-text, #1d1b20); font-size: var(--xl-type-body-large-size, 16px); }

.links-tab__sort { border: 0; background: transparent; font: inherit; color: inherit; padding: 0; cursor: pointer; display: inline-flex; gap: 4px; align-items: center; }
.links-tab__sort i { font-style: normal; color: var(--xl-color-secondary-text, #49454f); }

.links-tab__tick { width: 38px; }
.links-tab__num { text-align: right; }
.links-tab__menu { width: 48px; text-align: right; }
.links-tab__muted { color: var(--xl-color-secondary-text, #49454f); }
.links-tab__nowrap { white-space: nowrap; }

.links-tab__name { display: flex; align-items: center; gap: 11px; }
.links-tab__icon { width: 34px; height: 34px; display: inline-flex; align-items: center; justify-content: center; border-radius: var(--xl-radius-sm, 8px); background: color-mix(in srgb, var(--xl-color-primary, #5e2f78) 8%, white); color: var(--xl-color-primary, #5e2f78); flex: 0 0 34px; }
.links-tab__name-text { display: flex; flex-direction: column; min-width: 0; }
.links-tab__title { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.links-tab__name-text small { color: var(--xl-color-secondary-text, #49454f); }

.links-tab__none { padding: 30px; text-align: center; color: var(--xl-color-secondary-text, #49454f); }
tr.is-selected td { background: color-mix(in srgb, var(--xl-color-primary, #5e2f78) 6%, white); }

.links-tab__foot { padding: 10px 12px; border-top: 1px solid var(--xl-color-outline-variant, #e7e0ec); }
.links-tab__cost { margin: 0; font-size: var(--xl-type-body-medium-size, 14px); }
.sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); }

/* ---- the cards layout ---- */

.links-tab__rows { display: flex; flex-direction: column; }

.links-tab__row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 20px;
  border-bottom: 1px solid var(--xl-color-outline-variant, #e7e0ec);
}
.links-tab__row:last-child { border-bottom: 0; }
.links-tab__row:hover,
.links-tab__row.has-menu { background: var(--xl-color-primary-container, #f5eff9); }

.links-tab__tile {
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--xl-radius-md, 12px);
  background: color-mix(in srgb, var(--xl-color-primary, #5e2f78) 8%, white);
  color: var(--xl-color-primary, #5e2f78);
  overflow: hidden;
}
.links-tab__tile img { width: 100%; height: 100%; object-fit: cover; }

.links-tab__row-text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.links-tab__row-text small { color: var(--xl-color-secondary-text, #49454f); }
.links-tab__where { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.links-tab__clicks {
  font-size: var(--xl-type-body-small-size, 12.5px);
  color: var(--xl-color-secondary-text, #49454f);
  white-space: nowrap;
}

.links-tab__row-actions { display: flex; align-items: center; gap: 10px; }

.links-tab__count {
  margin: 0;
  font-size: var(--xl-type-body-medium-size, 14px);
  color: var(--xl-color-secondary-text, #49454f);
}

.links-tab__cards-foot {
  padding: 10px 12px;
  border: 1px solid var(--xl-color-outline-variant, #e7e0ec);
  border-radius: var(--xl-radius-lg, 16px);
  background: #fff;
}

@media (max-width: 720px) {
  .links-tab__row { flex-wrap: wrap; }
  .links-tab__row-actions { width: 100%; justify-content: flex-end; }
}
</style>
